import React from "react";
import { motion } from "framer-motion";
import { FaHome, FaBuilding, FaHandshake, FaKey, FaMapMarkedAlt, FaChartLine } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const services = [
  {
    id: 1,
    title: "Property Sales",
    description: "Buy and sell premium properties with Solonet’s trusted real estate experts.",
    icon: <FaHome size={35} />,
  },
  {
    id: 2,
    title: "Property Rentals",
    description: "Find your perfect rental home or apartment with flexible payment plans.",
    icon: <FaBuilding size={35} />,
  },
  {
    id: 3,
    title: "Property Management",
    description: "We manage your property seamlessly — from maintenance to tenant handling.",
    icon: <FaHandshake size={35} />,
  },
  {
    id: 4,
    title: "Mortgage Assistance",
    description: "Get expert guidance on financing your dream property effortlessly.",
    icon: <FaKey size={35} />,
  },
  {
    id: 5,
    title: "Land Acquisition",
    description: "Secure prime lands for residential and commercial developments.",
    icon: <FaMapMarkedAlt size={35} />,
  },
  {
    id: 6,
    title: "Investment Consulting",
    description: "Maximize returns with our real estate investment advisory services.",
    icon: <FaChartLine size={35} />,
  },
];

const Services = () => {
  return (
    <section className="bg-gray-50 relative overflow-hidden">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Solonet Properties Services | Real Estate Solutions in Ghana</title>
        <meta
          name="description"
          content="Explore Solonet Properties' premium real estate services in Ghana: property sales, rentals, management, mortgage assistance, land acquisition, and investment consulting."
        />
        <meta name="keywords" content="Solonet Properties, Real Estate Services Ghana, Property Sales Ghana, Property Rentals Ghana, Kwame Sarfo" />
        <meta property="og:title" content="Solonet Properties Services | Real Estate Solutions in Ghana" />
        <meta property="og:description" content="Discover Solonet Properties’ comprehensive real estate services for buying, renting, and managing properties across Ghana." />
        <meta property="og:image" content="/images/services-og.jpg" />
        <meta property="og:url" content="https://solonetproperties.com/services" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* HEADER SECTION */}
      <div className="relative w-full h-[60vh] flex items-center justify-center">
        <img
          src="/images/header.jpg"
          alt="Solonet Services Header"
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
        <div className="absolute top-0 left-0 w-full h-full bg-black/50"></div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white drop-shadow-lg">
            Our Services
          </h1>
          <p className="text-gray-200 mt-4 text-lg md:text-xl max-w-2xl mx-auto">
            Solonet Properties delivers top-notch real estate solutions tailored to your needs across Ghana.
          </p>
        </motion.div>
      </div>

      {/* SERVICES GRID */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              className="bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center text-center transition duration-300 hover:shadow-2xl hover:scale-105"
            >
              <div className="bg-blue-100 p-5 rounded-full text-blue-600 mb-5">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-800">{service.title}</h3>
              <p className="text-gray-600 mt-3">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* BACKGROUND SHAPES */}
      <motion.div
        className="absolute top-10 -left-16 w-72 h-72 bg-blue-200 rounded-full filter blur-3xl opacity-30"
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-200 rounded-full filter blur-3xl opacity-25"
        animate={{ y: [0, -30, 0] }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </section>
  );
};

export default Services;
